import { useState } from "react";
import { useForm } from "@inertiajs/react";
import { Clock, Share2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/notice";
import FormField from "@/components/forms/FormField";
import MinutesField from "@/components/forms/MinutesField";
import StudentShareDialog from "@/components/shared/StudentShareDialog";
import { isValidMinuteTotal } from "@/lib/minutes";
import { useSubmitLock } from "@/lib/useSubmitLock";

/**
 * The two things a parent comes to do: log reading at home and share
 * their child's page. When either isn't possible yet (or any more), a
 * line says why instead of the card vanishing.
 */
export default function ParentActions({ student, schoolName, campaign }) {
  if (campaign.finished) {
    return <Notice tone="success">This read-a-thon is wrapped up. Final totals below.</Notice>;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {campaign.minutes_state === "open" ? <LogMinutesCard student={student} /> : <LoggingClosed campaign={campaign} />}
      <ShareCard student={student} schoolName={schoolName} />
    </div>
  );
}

function ActionCard({ tone, icon: Icon, title, children }) {
  const tones = {
    forest: "border-accent/25 bg-accent/5 [&_.action-icon]:bg-accent/15 [&_.action-icon]:text-accent",
    clay: "border-primary/25 bg-primary/5 [&_.action-icon]:bg-primary/15 [&_.action-icon]:text-primary",
    muted: "border-border bg-card [&_.action-icon]:bg-secondary [&_.action-icon]:text-muted-foreground",
  }[tone];
  return (
    <div className={`space-y-4 rounded-xl border p-5 sm:p-6 ${tones}`}>
      <div className="flex items-start gap-3">
        <span className="action-icon inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="pt-1.5 font-display text-lg font-semibold text-foreground">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function LoggingClosed({ campaign }) {
  const message = campaign.minutes_state === "not_started"
    ? `You can log reading from ${campaign.minutes_open_on}, when reading starts.`
    : `Logging closed at ${campaign.minutes_closed_at}.`;
  return (
    <ActionCard tone="muted" icon={Clock} title="Log reading minutes">
      <p className="text-sm text-muted-foreground">{message}</p>
    </ActionCard>
  );
}

function ShareCard({ student, schoolName }) {
  return (
    <ActionCard tone="clay" icon={Share2} title={`Share ${student.first_name}'s page`}>
      {student.shareable ? (
        <>
          <p className="text-sm text-muted-foreground">
            Send it to grandparents, aunts and uncles, or the family group chat. It's the biggest help with {student.first_name}'s fundraising.
          </p>
          <StudentShareDialog
            url={student.public_page_url}
            firstName={student.first_name}
            schoolName={schoolName}
            triggerLabel="Share donation page"
            triggerVariant="default"
            triggerSize="default"
            triggerFullWidth
          />
          <a href={student.public_page_path} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
            See what family will see
          </a>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {student.share_opens_on
            ? `You can share it from ${student.share_opens_on}, when donations open.`
            : `${student.first_name}'s donation page isn't available right now.`}
        </p>
      )}
    </ActionCard>
  );
}

function LogMinutesCard({ student }) {
  const [open, setOpen] = useState(false);
  const { data, setData, post, processing, reset } = useForm({ minutes: "", note: "" });
  const { submitting, startSubmit, finishSubmit } = useSubmitLock(processing);
  const minutesValid = isValidMinuteTotal(data.minutes);

  const submit = (e) => {
    e.preventDefault();
    if (!minutesValid || !startSubmit()) return;
    post(student.log_minutes_path, {
      preserveScroll: true,
      onSuccess: () => {
        setOpen(false);
        reset();
      },
      onFinish: finishSubmit,
    });
  };

  return (
    <ActionCard tone="forest" icon={Clock} title="Log reading minutes">
      <p className="text-sm text-muted-foreground">Minutes {student.first_name} read at home, on their own or read aloud.</p>
      {open ? (
        <form onSubmit={submit} className="space-y-3">
          <MinutesField name="minutes" required autoFocus placeholder="20" value={data.minutes} onChange={(e) => setData("minutes", e.target.value)} />
          <FormField name="note" label="Note (optional)" placeholder="Book title" value={data.note} onChange={(e) => setData("note", e.target.value)} />
          <div className="flex gap-2">
            <Button type="submit" variant="secondary" disabled={submitting || !minutesValid} className="flex-1">
              {submitting ? "Saving…" : "Log minutes"}
            </Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => setOpen(false)} disabled={submitting} aria-label="Cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" onClick={() => setOpen(true)} className="w-full">
          <Clock className="h-4 w-4" /> Log minutes read
        </Button>
      )}
    </ActionCard>
  );
}
